import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AppShell from "./AppShell";
import StatusBadge from "@/components/StatusBadge";

export default function Competitors() {
  const [url, setUrl] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/competitors").then((r) => {
      setItems(r.data);
      setLoading(false);
    });
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/competitors", { competitor_url: url });
      setItems((arr) => [res.data, ...arr]);
      setUrl("");
      toast.success("Competitor analysed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title="Competitors" subtitle="// comparative AI visibility">
      <div data-testid="competitors-page">
        <form onSubmit={add} className="flex gap-2 max-w-2xl" data-testid="competitor-form">
          <input
            type="text"
            required
            value={url}
            data-testid="competitor-url-input"
            placeholder="https://competitor.com"
            onChange={(e) => setUrl(e.target.value)}
            className="flex-1 bg-card border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-intl"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="competitor-add-button"
            className="px-6 py-3 bg-obsidian text-bone font-app font-bold uppercase tracking-wider text-sm hover:bg-intl transition-colors disabled:opacity-60"
          >
            {busy ? "Analysing…" : "Compare"}
          </button>
        </form>

        <div className="mt-8">
          {loading ? (
            <div className="font-mono text-sm">Loading…</div>
          ) : items.length === 0 ? (
            <div className="border border-dashed border-border p-12 text-center bg-card">
              <h2 className="font-marketing text-2xl">No competitors yet.</h2>
              <p className="mt-2 text-muted-foreground">Add one to see how their AI visibility compares to yours.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((c) => (
                <div key={c.id} className="border border-border bg-card p-6" data-testid={`competitor-${c.id}`}>
                  <div className="flex justify-between items-baseline">
                    <div>
                      <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">// {c.created_at?.slice(0, 16).replace("T", " ")}</div>
                      <div className="font-app text-xl">{c.competitor_url}</div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="font-mono text-4xl">{c.final_score}<span className="text-muted-foreground text-base">/100</span></div>
                      <StatusBadge label={c.status_label} />
                    </div>
                  </div>
                  {c.verdict && <p className="mt-4 text-muted-foreground leading-relaxed">{c.verdict}</p>}
                  <div className="mt-6 grid grid-cols-3 md:grid-cols-9 gap-1">
                    {(c.categories || []).map((cat) => (
                      <div key={cat.key} className="text-center">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground truncate" title={cat.name}>{cat.name.split(" ")[0]}</div>
                        <div className="mt-1 h-12 flex items-end">
                          <div className="w-full bg-intl" style={{ height: `${cat.score}%` }} />
                        </div>
                        <div className="font-mono text-xs mt-1">{cat.score}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
